import type { PresetListing } from "@pax/api-contract/presets/v1alpha1";
import { usePanelPresence } from "@pax/ui";
import { type ReactNode, useCallback, useState } from "react";
import { LegalLine } from "@/components/LegalLine";
import { sortCatalog } from "../ordering";
import { FeaturedScenario } from "./FeaturedScenario";
import { StoreRail, StoreRailSkeleton } from "./StoreRail";
import { CreateWay, SearchWay } from "./StoreWays";
import { WriteScenarioPanel } from "./WriteScenarioPanel";

/** How many scenarios the store argues for before you choose one. */
const FEATURED = 5;

/**
 * The front of the store: one scenario at the size of the screen, and the
 * catalog on a shelf laid over it.
 *
 * The featured scenario is not the first item on the page — it is the page,
 * and scrolling raises the shelf over it. Nothing here is a field: searching
 * is a link to the search wall, so one control can never silently turn this
 * screen into a different one.
 */
export function StoreFront({ listings }: { listings: PresetListing[] }) {
  const [writing, setWriting] = useState(false);
  const closeWriting = useCallback(() => setWriting(false), []);
  const panel = usePanelPresence(writing);

  return (
    <>
      <FeaturedScenario
        listings={sortCatalog(listings, "played").slice(0, FEATURED)}
        controls={
          <>
            <SearchWay />
            <CreateWay onPress={() => setWriting(true)} />
          </>
        }
      />
      <Shelf>
        <StoreRail sort="played" listings={listings} ranked />
        <div className="mt-9 sm:mt-11">
          <StoreRail sort="added" listings={listings} />
        </div>
      </Shelf>
      {panel.mounted && (
        <WriteScenarioPanel leaving={panel.leaving} onLeft={panel.onLeft} onClose={closeWriting} />
      )}
    </>
  );
}

export function StoreFrontSkeleton() {
  return (
    <>
      <div aria-busy className="sticky top-0 h-(--landing-hero) bg-foreground/5" />
      <Shelf>
        <StoreRailSkeleton ranked />
        <div className="mt-9 sm:mt-11">
          <StoreRailSkeleton />
        </div>
      </Shelf>
    </>
  );
}

/**
 * The catalog, as a sheet over the artwork rather than a card on it.
 *
 * At rest its top edge is a straight line across the picture; by the time it
 * has risen far enough to meet the bar its corners are the sheet's own, so it
 * arrives as the page rather than as something laid on one. `.store-shelf`
 * draws that from the scroll position itself.
 *
 * It is at least a screen tall whatever is on it, which is what guarantees
 * there is always enough scroll to cover the artwork completely.
 *
 * Its ground runs to the edges because it is the page; the rows on it do not.
 * They are held off the window by the reading width, so a row ends in a
 * margin rather than against the frame, and it ends in the same margin
 * whichever arrangement the ways are in.
 */
function Shelf({ children }: { children: ReactNode }) {
  return (
    <div className="store-shelf relative flex min-h-full flex-col bg-screen pt-7 shadow-[0_-12px_36px_rgb(0_0_0/0.18)]">
      <div className="app-reading-width">{children}</div>
      <LegalLine />
    </div>
  );
}
